import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, User, ArrowLeft, Loader2 } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import SEOHead from "@/components/SEOHead";
import api from "@/lib/api";

const PAGE_SIZE = 8;

export default function CategoryPage() {
  const { slug } = useParams();
  const [articles, setArticles] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  const loadArticles = useCallback(async (cat, pageNum, append = false) => {
    if (!cat) return;
    const skip = pageNum * PAGE_SIZE;
    try {
      const res = await api.get(`/articles?category_id=${cat.id}&limit=${PAGE_SIZE}&skip=${skip}`);
      const newArticles = res.data.articles || [];
      setTotal(res.data.total || 0);
      setArticles(prev => append ? [...prev, ...newArticles] : newArticles);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setArticles([]);
      setPage(0);
      try {
        const catRes = await api.get("/categories");
        const cats = catRes.data || [];
        const cat = cats.find(c => c.slug === slug);
        setCategory(cat);
        if (cat) await loadArticles(cat, 0);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [slug, loadArticles]);

  const handleLoadMore = async () => {
    const nextPage = page + 1;
    setLoadingMore(true);
    await loadArticles(category, nextPage, true);
    setPage(nextPage);
    setLoadingMore(false);
  };

  const hasMore = articles.length < total;

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Skeleton className="h-10 w-64 mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-64" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="category-page">
      <SEOHead
        title={category?.name || slug?.replace(/-/g, " ")}
        description={category?.description}
        type="website"
      />

      <Link to="/" className="inline-flex items-center gap-2 text-sm font-sans text-muted-foreground hover:text-foreground mb-6" data-testid="back-home">
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </Link>

      <div className="mb-10">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-foreground">
          {category?.name || slug?.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
        </h1>
        {category?.description && <p className="text-base font-sans text-muted-foreground mt-3 max-w-2xl">{category.description}</p>}
        {total > 0 && <p className="text-sm font-sans text-muted-foreground mt-2">{total} article{total !== 1 ? "s" : ""}</p>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
        <div className="lg:col-span-8">
          {articles.length === 0 ? (
            <p className="text-muted-foreground font-sans py-12 text-center">No articles found in this category.</p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {articles.map((art) => (
                  <Link key={art.id} to={`/article/${art.id}`} className="flex flex-col gap-4 group" data-testid={`cat-article-${art.id}`}>
                    {art.featured_image && (
                      <div className="aspect-[4/3] overflow-hidden border border-slate-200 dark:border-slate-700">
                        <img src={art.featured_image} alt={art.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-xl font-serif text-foreground group-hover:text-[#1E88E5] transition-colors leading-snug line-clamp-2">{art.title}</h3>
                      <p className="text-sm font-sans text-muted-foreground mt-2 line-clamp-2">{art.summary}</p>
                      <div className="flex items-center gap-3 mt-3 text-xs font-sans text-muted-foreground">
                        {art.author_name && <span className="flex items-center gap-1"><User className="h-3 w-3" />{art.author_name}</span>}
                        {art.reading_time && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{art.reading_time}</span>}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Load More / Pagination */}
              {hasMore && (
                <div className="mt-10 text-center" data-testid="load-more-container">
                  <Button
                    variant="outline"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="font-sans text-sm px-8"
                    data-testid="load-more-btn"
                  >
                    {loadingMore ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading...</>
                    ) : (
                      `Load More (${articles.length} of ${total})`
                    )}
                  </Button>
                </div>
              )}

              {!hasMore && articles.length > 0 && (
                <p className="mt-8 text-center text-xs font-sans text-muted-foreground">
                  Showing all {articles.length} articles
                </p>
              )}
            </>
          )}
        </div>
        <div className="hidden lg:block lg:col-span-4">
          <Sidebar />
        </div>
      </div>
    </div>
  );
}
